import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { filter, Observable, of, shareReplay } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import SafeUrlAssembler from 'safe-url-assembler';
import { AppState } from '../../../app.state';
import { ApiEndpointService } from '../../../core/services/api-endpoint.service';
import { HttpClientService } from '../../../core/services/http/http-client.service';
import { SharedService } from '../../../core/services/shared.service';
import { Customer } from '../../models/customer/customer.model';
import { User } from './models/user.model';
import { DefaultTabIndex } from 'app/account/models/defaultTabIndex.model';

@Injectable()
export class UserService {

    public constructor(private httpClientService: HttpClientService,
                       private apiEndpointService: ApiEndpointService,
                       private sharedService: SharedService,
                       private translateService: TranslateService,
                       private readonly appState: AppState,
    ) {
    }

    /**
     * Gets a user based on the provided token in the request header.
     *
     * @returns {Observable<any>}
     */
    public getUser(): Observable<User> {
        const endpoint = SafeUrlAssembler().template(this.apiEndpointService.getEndpoint('getUser')).toString();
        return this.httpClientService.get<User>(endpoint)
                   .pipe(
                       shareReplay(1),
                       filter(user => !!user),
                       tap((user) => {
                           // this.sharedService.userSubject.next(user);
                       }),
                   );
    }

    public getCurrentUser(): Observable<any> {
        const user = this.appState.user$();
        if (user) {
            return of({
                username: user.UserName,
                sessionid: user.MobileSessionID,
                defaultLocation: user.DefaultLocation,
                isAdmin: user.IsAdmin,
                isGuest: user.IsGuest,
                userId: user.UserId,
            });
        }

        return this.getUser().pipe(
            map((user) => ({
                username: user.UserName,
                sessionid: user.MobileSessionID,
                defaultLocation: user.DefaultLocation,
                isAdmin: user.IsAdmin,
                isGuest: user.IsGuest,
                userId: user.UserId,
            })),
        );
    }

    /**
     * Gets a customer (account).
     * The customer ID could be found in a User object.
     *
     * @returns {Observable<Customer>}
     */
    public getCustomer(): Observable<Customer> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('getCustomer'))
            .toString();

        return this.httpClientService.get<Customer>(endpoint);
    }

    /**
     * Gets terms and conditions.
     *
     * @returns {Observable<any>}
     */
    public getTerms(): Observable<any> {
        const endpoint = SafeUrlAssembler().template(this.apiEndpointService.getEndpoint('getTerms')).toString();
        return this.httpClientService.get<any>(endpoint);
    }

    public getTerm(id: string): Observable<any> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('getTerms'))
            .param('id', id)
            .toString();
        return this.httpClientService.get<any>(endpoint);
    }

    /**
     * Gets Signed terms and conditions.
     *
     * @returns {Observable<any>}
     */
    public getTermsSigned(): Observable<any> {
        const endpoint = SafeUrlAssembler().template(this.apiEndpointService.getEndpoint('getTermsSigned')).toString();
        return this.httpClientService.get<any>(endpoint);
    }

    /**
     * Save terms and conditions.
     *
     * @returns {Observable<any>}
     */
    public saveTerms(data): Observable<any> {
        const endpoint = SafeUrlAssembler().template(this.apiEndpointService.getEndpoint('saveTerms')).toString();
        return this.httpClientService.post<any>(endpoint, data);
    }

    public isHKUser(user: User) {
        if (!user) {
            throw new Error('Cannot determine if a user is a DE/GB user. User object is missing');
        }

        return ['HK'].includes(user?.CountryCode);
    }

    public isDEGBUser(user: User) {
        if (!user) {
            throw new Error('Cannot determine if a user is a DE/GB user. User object is missing');
        }

        return ['DE', 'GB'].includes(user?.CountryCode);
    }

    public isDEUser(user: User) {
        if (!user) {
            throw new Error('Cannot determine if a user is a DE user. User object is missing');
        }

        return ['DE'].includes(user?.CountryCode);
    }
    public isDEFRITUser(user: User) {
        if (!user) {
            throw new Error('Cannot determine if a user is a DE or FR or IT user. User object is missing');
        }

        return ['DE','FR','IT'].includes(user?.CountryCode);
    }
    public isUserEU(user: User) {
        if (!user) {
            throw new Error('Cannot determine if a user is a EU user. User object is missing');
        }

        return ['GB', 'DE', 'FR', 'IT'].includes(user?.CountryCode);
    }
    public isFRITUser(user: User) {
        if (!user) {
            throw new Error('Cannot determine if a user is a FR or IT user. User object is missing');
        }

        return ['FR','IT'].includes(user?.CountryCode);
    }
    public isGBUser(user: User) {
        if (!user) {
            throw new Error('Cannot determine if a user is a GB user. User object is missing');
        }

        return ['GB'].includes(user?.CountryCode);
    }

    /**
     * Check if the specified country code is a part of EU countries.
     * @param countryCode
     */
    public isEUUser(countryCode: string): boolean {
        if (!countryCode) {
            throw new Error('Cannot determine if a user is a EU user. Country code is missing.');
        }

        return ['GB', 'DE', 'FR', 'IT'].includes(countryCode);
    }

    /**
     * Check if the specified country code is using EUR currency.
     * @param countryCode
     */
    public isEURUser(countryCode: string): boolean {
        if (!countryCode) {
            throw new Error('Cannot determine if a user is using EUR currency. Country code is missing.');
        }

        return ['DE', 'FR', 'IT'].includes(countryCode);
    }

    public isDomesticOnly(user: User) {
        if (!user) {
            throw new Error('Cannot determine if a user is allowed to ship domestic only. User object is missing');
        }

        return (user.IsUPSDomestic && !user.IsUPSInternational)
            || (user.IsFedExDomestic && !user.IsFedExInternational)
            || (user.IsUSPSDomestic && !user.IsUSPSInternational);
    }

    public isInternationalOnly(user: User) {
        if (!user) {
            throw new Error('Cannot determine if a user is allowed to ship international only. User object is missing');
        }

        return (!user.IsUPSDomestic && user.IsUPSInternational)
            || (!user.IsFedExDomestic && user.IsFedExInternational)
            || (!user.IsUSPSDomestic && user.IsUSPSInternational);
    }

    public isUPSOnly(user: User) {
        if (!user) {
            throw new Error('Cannot determine if a user is allowed to ship UPS only. User object is missing');
        }

        return (user.IsUPSDomestic || user.IsUPSInternational)
            && (!user.IsFedExDomestic && !user.IsFedExInternational)
            && (!user.IsUSPSDomestic && !user.IsUSPSInternational);
    }

    public isFedExOnly(user: User) {
        if (!user) {
            throw new Error('Cannot determine if a user is allowed to ship FedEx only. User object is missing');
        }

        return (!user.IsUPSDomestic && !user.IsUPSInternational)
            && (user.IsFedExDomestic || user.IsFedExInternational)
            && (!user.IsUSPSDomestic && !user.IsUSPSInternational);
    }

    public isUSPSOnly(user: User) {
        if (!user) {
            throw new Error('Cannot determine if a user is allowed to ship USPS only. User object is missing');
        }

        return (!user.IsUPSDomestic && !user.IsUPSInternational)
            && (!user.IsFedExDomestic && !user.IsFedExInternational)
            && (user.IsUSPSDomestic || user.IsUSPSInternational);
    }

    public standardizeLocalizedNumber(user: User, value: string): number {
        if (!user || value === '' || value === null || typeof value === 'undefined') {
            return 0;
        }

        if (!this.isDEGBUser(user)) {
            return +value;
        }

        const numberFragments = value.toString().split(/,/);

        // [MV3-1704] Treat ',' and '.' the same for DE/GB users.
        // For example, 1,5 and 1.5 are the same - treated as 1.5.
        const integerPart = numberFragments[0];
        // const integerPart = numberFragments[0].replace(/\./g, '');

        const fractionalPart = numberFragments[1] || '0';

        const numberText = `${ integerPart }.${ fractionalPart }`;
        return parseFloat(numberText);
    }

    /**
     * Saves the user data.
     *
     * @param {string} data
     * @returns {Observable<any>}
     */
    public saveUser(data: User): Observable<any> {
        const endpoint = SafeUrlAssembler().template(this.apiEndpointService.getEndpoint('saveUser')).toString();
        return this.httpClientService.post<any>(endpoint, data);
    }

    public updateDefaultTabIndex(DefaultUploadTabIndex: DefaultTabIndex) {
        const endpoint = SafeUrlAssembler().template(this.apiEndpointService.getEndpoint('updateDefaultTabIndex')).toString();
        return this.httpClientService.put<any>(endpoint, DefaultUploadTabIndex);
    }

    public updateUserDefaultTabIndex(tabIndex: number): Observable<any> {
        return this.updateDefaultTabIndex({ DefaultUploadTabIndex: tabIndex });
    }

    public isUSCustomer(customer: Customer) {
        return customer.ShippingStationId.toString() === '15';
    }

    public getCustomerLanguage(customer: Customer) {
        switch (customer?.ShippingStationId?.toString()) {
            case '5':
                return 'de';
            case '6':
                return 'en-gb';
            case '15':
                return 'en';
            case '16':
                return 'fr';
            case '17':
                return 'it';
            case '13':
                return 'zh-hk';
            default:
                return 'en';
        }
    }

    public isGBCustomer(customer: Customer) {
        return customer.ShippingStationId.toString() === '6';
    }

    public getStatusCount(startDate: string, endDate: string): Observable<any> {
        const endpoint = SafeUrlAssembler().template(this.apiEndpointService.getEndpoint('getStatusCount'))
            // const endpoint = SafeUrlAssembler().template(`https://api-uat.parcelpro.com/v3.0/api/marketing/banners`)
                                           .query({
                                               startDate,
                                               endDate,
                                           })
                                           .toString();
        return this.httpClientService.get<any>(endpoint);
    }

    public GetRestrictShipFromAddressChangeMessage(countryCode: string) {
        let RestrictMessage: string;
        if (countryCode === 'DE') {
            RestrictMessage = this.translateService.instant('RestrictShipFromAddressChangeMessage')
                                  .replace('<EMAIL>', 'risksolutionsde@ups.com')
                                  .replace('<PHONE>', '+49 69 6630 8330');
        } else if (countryCode === 'GB') {
            RestrictMessage = this.translateService.instant('RestrictShipFromAddressChangeMessage')
                                  .replace('<EMAIL>', 'risksolutionsuk@ups.com')
                                  .replace('<PHONE>', '+44 20 7949 0144');
        } else if (countryCode === 'IT') {
            RestrictMessage = this.translateService.instant('RestrictShipFromAddressChangeMessage')
                                  .replace('<EMAIL>', 'risksolutionsit@ups.com')
                                  .replace('<PHONE>', '+39 02 4527 9099');
        } else if (countryCode === 'FR') {
            RestrictMessage = this.translateService.instant('RestrictShipFromAddressChangeMessage')
                                  .replace('<EMAIL>', 'risksolutionsfr@ups.com')
                                  .replace('<PHONE>', '+33 1 73 01 84 55');
        } else if (countryCode === 'HK') {
            RestrictMessage = this.translateService.instant('RestrictShipFromAddressChangeMessage')
                                  .replace('<EMAIL>', 'riskolutionsuk@ups.com')
                                  .replace('<PHONE>', '+44 20 7949 0144');
        } else {
            RestrictMessage = this.translateService.instant('RestrictShipFromAddressChangeMessage')
                                  .replace('<EMAIL>', 'risksolutionsuk@ups.com')
                                  .replace('<PHONE>', '+44 20 7949 0144');
        }
        return RestrictMessage;
    }
}
